import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ResponsiveSidebar from './ResponsiveSidebar';
import ResponsiveTopHeader from './ResponsiveTopHeader';
import MobileTopNav from './MobileTopNav';
import MobileMenuDrawer from './MobileMenuDrawer';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../contexts/AuthContext';
import { LAYOUT } from '../constants/layout';
import ForcePasswordChangeModal from '../components/modals/ForcePasswordChangeModal';

const ResponsiveAppShell = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Debug: Log state changes
  useEffect(() => {
    console.log('[ResponsiveAppShell] mobileMenuOpen state:', mobileMenuOpen);
  }, [mobileMenuOpen]);
  const location = useLocation();
  const navigate = useNavigate();
  const { switchBusiness, activeBusiness } = useBusiness();
  const { user, passwordResetRequired } = useAuth();
  const isInitialMount = useRef(true);

  // Extract businessId from URL
  const businessIdFromUrl = useMemo(() => {
    const match = location.pathname.match(/\/business\/([^\/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  // Set active business from URL on mount or URL change with validation
  useEffect(() => {
    if (businessIdFromUrl && 
        typeof businessIdFromUrl === 'string' && 
        businessIdFromUrl !== 'undefined' &&
        !businessIdFromUrl.includes('undefined')) {
      
      // Validate user has access to this business
      const userBusinessesStr = localStorage.getItem('user_businesses');
      if (userBusinessesStr) {
        try {
          const userBusinesses = JSON.parse(userBusinessesStr);
          const hasAccess = userBusinesses.some(b => b.id === businessIdFromUrl);
          
          if (!hasAccess) {
            console.error('[ResponsiveAppShell] User does not have access to business:', businessIdFromUrl);
            // Redirect to first available business or login
            if (userBusinesses.length > 0) {
              const firstBusiness = userBusinesses[0];
              const userRole = user?.role?.toUpperCase() === 'BUSINESS_OWNER' ? 'owner' : 'staff';
              navigate(`/business/${firstBusiness.id}/dashboard/${userRole}`, { replace: true });
            } else {
              navigate('/login', { replace: true });
            }
            return;
          }
        } catch (e) {
          console.error('[ResponsiveAppShell] Failed to parse user businesses:', e);
        }
      }
      
      // Only call switchBusiness if business ID is actually different
      if (isInitialMount.current || !activeBusiness || activeBusiness.id !== businessIdFromUrl) {
        isInitialMount.current = false;
        switchBusiness(businessIdFromUrl).catch(error => {
          console.error('[ResponsiveAppShell] Failed to switch business:', error);
          // Redirect to login on 403 or other errors
          if (error.response?.status === 403 || error.response?.status === 401) {
            navigate('/login', { replace: true });
          }
        });
      }
    }
  }, [businessIdFromUrl, navigate, user]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-screen bg-[#F0F2F5] overflow-hidden">
      {/* DESKTOP ONLY: Responsive Sidebar - Fixed, Full Height */}
      <div className="hidden lg:flex lg:flex-shrink-0 p-1">
        <ResponsiveSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isMobile={false} />
      </div>

      {/* MOBILE ONLY: Off-Canvas Drawer - Fixed position, slides in from left */}
      <MobileMenuDrawer isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        <ResponsiveSidebar isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} isMobile={true} />
      </MobileMenuDrawer>

      {/* Main Content Area - With horizontal gap from sidebar */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden p-1 pl-0 lg:pl-1">
        {/* DESKTOP ONLY: Top Header (B1) */}
        <div className="hidden lg:block flex-shrink-0 bg-white rounded-t-lg shadow-sm overflow-hidden">
          <ResponsiveTopHeader onMenuClick={() => setSidebarOpen(true)} />
        </div>

        {/* MOBILE ONLY: Mobile Top Nav - Fixed at top */}
        <div className="lg:hidden flex-shrink-0">
          <MobileTopNav onMenuClick={() => {
            console.log('[Mobile Menu] Hamburger clicked, toggling drawer...');
            setMobileMenuOpen(prev => {
              console.log('[Mobile Menu] State changing from', prev, 'to', !prev);
              return !prev;
            });
          }} />
        </div>

        {/* Content Area (B2 + T1) - With vertical gap from B1 */}
        <main className="flex-1 overflow-auto pt-16 lg:pt-0 lg:rounded-b-lg lg:shadow-sm bg-[#F0F2F5]">
          {children}
        </main>
      </div>

      {/* Force password change modal for staff with temporary passwords */}
      <ForcePasswordChangeModal open={passwordResetRequired} />
    </div>
  );
};

export default ResponsiveAppShell;